import { useState } from "react";

import { cycleInfo, cycleOn, periodDay } from "../domain/cycle";
import { DIARY_WORDS } from "../domain/cycleWords";
import { dayLabel, parseDay, todayStr } from "../domain/date";
import { sessionKeyFor, weekMapFor } from "../domain/plan";
import { CHOICE_GROUPS, SOAKED, TOGGLES, choice, hasFlow, symptomsOf, type ChoiceGroup } from "../domain/symptoms";
import { setAppBadge } from "../lib/device";
import { touchDay } from "../store/dayLog";
import { useKaya } from "../store/kayaStore";
import {
  clearSymptoms,
  setCycleSettings,
  setPeriodStart,
  setSessionOverride,
  setSymptom,
  setSymptomChoice,
} from "../store/toggles";
import { ActionBar } from "./ActionBar";
import { Chip, ChipRow } from "./Chip";
import { I } from "./icons";
import { NavRow } from "./Rows";
import { Segmented } from "./Segmented";
import { Sheet } from "./Sheet";
import "./CycleSheet.css";

type Session = "gentle" | "normal" | "rest";

/**
 * The day's cycle log, opened from Today's cycle row and from Progress › Cycle (ADR-033 decision
 * 6): whether a period started, then taps — flow, pain, breast pain, energy, how it feels, mood,
 * sleep. Every tap is written as it is made, so Save only closes.
 *
 * Every group is asked on any day (ADR-060): breast pain, back ache or a headache come in the
 * days before a period as often as during it, and a gate on period days hid exactly the days a
 * pattern is made of. On a period day the session choice stays too: gentle, normal, or rest that
 * keeps the streak. The symptom diary opens this for a past day, so a day can be filled in late.
 *
 * Starts go through `setPeriodStart`, answers through `setSymptomChoice`/`setSymptom`/
 * `clearSymptoms`, the session through `setSessionOverride`, and the period-length refinement
 * through `setCycleSettings`, so each leaves the stamps the merge needs (ADR-019, ADR-033).
 */
export function CycleSheet({ open, onClose, ds }: { open: boolean; onClose: () => void; ds: string }) {
  const { S, update, showToast, checkBadges } = useKaya();
  const [ended, setEnded] = useState(false);

  if (!open || !cycleOn(S)) return null;

  const ci = cycleInfo(S, ds);
  const keys = symptomsOf(S, ds);
  const isToday = ds === todayStr();
  const startedHere = (S.cycle.starts || []).includes(ds);
  const onPeriod = periodDay(S, ds);
  const last = ci?.last ?? null;
  const log = S.logs[ds];

  const head = isToday ? "Today" : dayLabel(ds);
  const title =
    ci?.day == null ? head : onPeriod ? `${head} · period day ${ci.day}` : `${head} · cycle day ${ci.day}`;

  const toggleStart = (on: boolean) => {
    update((d) => setPeriodStart(d, ds, on));
    if (on) {
      showToast("Logged — gentle plan for today");
      checkBadges();
    } else showToast("Removed");
  };

  const pick = (g: ChoiceGroup, value: string) =>
    update((d) => setSymptomChoice(d, ds, g.id, choice(keys, g.id) === value ? null : value));
  const flip = (key: string) => update((d) => setSymptom(d, ds, key, !keys.includes(key)));

  const markEnded = () => {
    if (!last) return;
    update((d) => {
      d.cycle.ends = d.cycle.ends || {};
      d.cycle.ends[last] = ds;
      const len = Math.floor((parseDay(ds).getTime() - parseDay(last).getTime()) / 864e5) + 1;
      setCycleSettings(d, { periodLen: Math.max(2, Math.min(8, Math.round((d.cycle.periodLen + len) / 2))) });
    });
    setEnded(true);
    showToast("Noted — normal plan resumes tomorrow");
  };

  // Rest cannot be taken back once chosen: the merge ORs `periodSkip` across devices, so an
  // un-rest would come straight back on the next sync. The choice goes once it is made.
  const key = sessionKeyFor(S, ds);
  const session: Session = log?.periodSkip ? "rest" : key === "P" ? "gentle" : "normal";
  const chooseSession = (c: Session) => {
    if (c === session) return;
    const w = weekMapFor(S)[parseDay(ds).getDay()];
    // A period day already swaps a working day for the gentle session; on a scheduled rest day
    // there is nothing to swap, so gentle has to be asked for by name.
    if (c === "gentle") update((d) => setSessionOverride(d, ds, w === "R" ? "P" : null));
    else if (c === "normal") update((d) => setSessionOverride(d, ds, w === "R" ? "C" : w));
    else {
      update((d) => void (touchDay(d, ds).periodSkip = true));
      setAppBadge(0);
      showToast("Rest logged — streak protected");
    }
  };

  const group = (g: ChoiceGroup) => {
    const v = choice(keys, g.id);
    return (
      <div className="cs-field" key={g.id}>
        <p className="cs-label">{g.label}</p>
        <ChipRow ariaLabel={g.label}>
          {g.options.map((o) => (
            <Chip key={o.key} selected={v === o.value} onClick={() => pick(g, o.value)}>
              {o.label}
            </Chip>
          ))}
        </ChipRow>
      </div>
    );
  };
  const G = (id: ChoiceGroup["id"]) => CHOICE_GROUPS.find((g) => g.id === id);
  const shown = (g: ChoiceGroup | undefined): g is ChoiceGroup => !!g;
  const heavy = choice(keys, "flow") === "heavy";
  const soaked = TOGGLES.find((t) => t.key === SOAKED);
  const feel = TOGGLES.filter((t) => t.key !== SOAKED);
  const flow = G("flow");
  // Flow cannot be judged before the period starts (the owner, 6 Oct): asked on a period day, on
  // the day the start is logged, or where a day already holds flow, so an old day still reads.
  const flowShown = shown(flow) && (onPeriod || startedHere || hasFlow(keys));
  const pain = G("pain");
  const breast = G("breast");
  const energy = G("energy");
  const mood = G("mood");
  const sleep = G("sleep");

  return (
    <Sheet
      title={title}
      sub={dayLabel(ds)}
      onClose={onClose}
      footer={
        <ActionBar
          inSheet
          secondary={{ label: "Feeling fine", onClick: () => update((d) => clearSymptoms(d, ds)) }}
          primary={{ label: "Save", onClick: onClose }}
        />
      }
    >
      <div className="cs">
        <NavRow
          kind="toggle"
          title={isToday ? "Period started today" : "Period started this day"}
          subtitle={onPeriod && last ? `${dayLabel(last)} · day 1` : undefined}
          checked={startedHere}
          onChange={toggleStart}
        />
        {onPeriod && last && last !== ds && !ended ? (
          <NavRow title="Period ended" subtitle={`Mark ${isToday ? "today" : dayLabel(ds)} as the last day`} icon={I.check} onClick={markEnded} />
        ) : null}

        {onPeriod ? (
          <div className="cs-field">
            <p className="cs-label">Session</p>
            {session === "rest" ? (
              <p className="cs-note">Resting today. Your streak is protected.</p>
            ) : (
              <Segmented<Session>
                ariaLabel="Today's session"
                value={session}
                onChange={chooseSession}
                options={[
                  { value: "gentle", label: "Gentle" },
                  { value: "normal", label: "Normal" },
                  { value: "rest", label: "Rest" },
                ]}
              />
            )}
          </div>
        ) : null}

        {flowShown && flow ? group(flow) : null}
        {flowShown && heavy && soaked ? (
          <NavRow
            kind="toggle"
            title={soaked.label}
            checked={keys.includes(SOAKED)}
            onChange={(on) => update((d) => setSymptom(d, ds, SOAKED, on))}
          />
        ) : null}
        {shown(pain) ? group(pain) : null}
        {shown(breast) ? group(breast) : null}
        {shown(energy) ? group(energy) : null}
        <div className="cs-field">
          <p className="cs-label">Feel · any that apply</p>
          <ChipRow ariaLabel="Feel, any that apply">
            {feel.map((t) => (
              <Chip key={t.key} selected={keys.includes(t.key)} onClick={() => flip(t.key)}>
                {t.label}
              </Chip>
            ))}
          </ChipRow>
        </div>
        {shown(mood) ? group(mood) : null}
        {shown(sleep) ? group(sleep) : null}

        <p className="cs-note">{DIARY_WORDS.sheetNote}</p>
      </div>
    </Sheet>
  );
}
